import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Title2,
  Body1,
  Spinner,
  Badge,
  Button,
  Input,
  Combobox,
  Option,
  Switch,
  Field,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Toaster,
  useToastController,
  useId,
  Toast,
  ToastTitle,
  ToastBody,
  makeStyles,
  tokens
} from "@fluentui/react-components";
import { fetchWithRetry } from "../lib/fetchWithRetry";
import type { AdminUser } from "./AdminUsers";

interface Delegation {
  id: string;
  managerId: string;
  managerName: string;
  managerEmail: string;
  substituteId: string;
  substituteName: string;
  substituteEmail: string;
  startDate: string;
  endDate: string;
  isActive: boolean;
  state: "active" | "upcoming" | "expired" | "inactive";
  createdAt: string;
  updatedAt: string;
}

type FilterKey = "all" | "active" | "upcoming" | "expired" | "inactive";

const useStyles = makeStyles({
  headerRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalS
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalM,
    flexWrap: "wrap"
  },
  filterChips: {
    display: "flex",
    gap: tokens.spacingHorizontalXS,
    flexWrap: "wrap"
  },
  empty: {
    padding: tokens.spacingVerticalXL,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium
  },
  error: { color: tokens.colorPaletteRedForeground1 },
  warn: { color: tokens.colorPaletteDarkOrangeForeground1 },
  dialogBody: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    minWidth: "420px"
  },
  dateRow: {
    display: "flex",
    gap: tokens.spacingHorizontalM
  }
});

function stateBadge(s: Delegation["state"]) {
  if (s === "active") return <Badge appearance="filled" color="success">Active now</Badge>;
  if (s === "upcoming") return <Badge appearance="filled" color="informative">Upcoming</Badge>;
  if (s === "expired") return <Badge appearance="outline" color="subtle">Expired</Badge>;
  return <Badge appearance="outline" color="severe">Inactive</Badge>;
}

function formatDateRange(start: string, end: string): string {
  if (start === end) return start;
  return `${start} → ${end}`;
}

export default function AdminDelegations() {
  const styles = useStyles();
  const toasterId = useId("admin-delegations-toaster");
  const { dispatchToast } = useToastController(toasterId);

  const [delegations, setDelegations] = useState<Delegation[] | null>(null);
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warming, setWarming] = useState(false);
  const [filter, setFilter] = useState<FilterKey>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Delegation | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Delegation | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [delResp, userResp] = await Promise.all([
        fetchWithRetry("/api/manage-delegations", undefined, { onWarming: () => setWarming(true) }),
        fetchWithRetry("/api/manage-users")
      ]);
      if (!delResp.ok) {
        const body = await delResp.json().catch(() => ({}));
        throw new Error(body.error ?? `delegations HTTP ${delResp.status}`);
      }
      if (!userResp.ok) {
        const body = await userResp.json().catch(() => ({}));
        throw new Error(body.error ?? `users HTTP ${userResp.status}`);
      }
      const delJson = await delResp.json() as { delegations: Delegation[] };
      const userJson = await userResp.json() as { users: AdminUser[] };
      setDelegations(delJson.delegations);
      setUsers(userJson.users);
      setWarming(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  useEffect(() => {
    setDelegations(null);
    setUsers(null);
    setWarming(false);
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!delegations) return [];
    if (filter === "all") return delegations;
    return delegations.filter((d) => d.state === filter);
  }, [delegations, filter]);

  const counts = useMemo(() => {
    const init = { all: 0, active: 0, upcoming: 0, expired: 0, inactive: 0 } as Record<FilterKey, number>;
    if (!delegations) return init;
    init.all = delegations.length;
    for (const d of delegations) init[d.state]++;
    return init;
  }, [delegations]);

  async function handleToggleActive(d: Delegation) {
    try {
      const r = await fetch(`/api/manage-delegations/${encodeURIComponent(d.id)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isActive: !d.isActive })
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${r.status}`);
      }
      dispatchToast(
        <Toast><ToastTitle>{d.isActive ? "Paused" : "Resumed"}</ToastTitle></Toast>,
        { intent: "success" }
      );
      await load();
    } catch (e) {
      dispatchToast(
        <Toast><ToastTitle>Couldn't update</ToastTitle><ToastBody>{e instanceof Error ? e.message : String(e)}</ToastBody></Toast>,
        { intent: "error" }
      );
    }
  }

  if (error) {
    return (
      <div>
        <Toaster toasterId={toasterId} />
        <Title2 as="h2" block>Substitute delegations</Title2>
        <Body1 as="p" className={styles.error}>Error: {error}</Body1>
      </div>
    );
  }

  if (!delegations || !users) {
    return (
      <div>
        <Toaster toasterId={toasterId} />
        <Title2 as="h2" block>Substitute delegations</Title2>
        <Spinner
          size="tiny"
          label={warming ? "Waking up the database… 30–60s the first time after hours." : "Loading…"}
        />
      </div>
    );
  }

  const activeUsers = users.filter((u) => u.isActive);

  return (
    <div>
      <Toaster toasterId={toasterId} />
      <div className={styles.headerRow}>
        <div>
          <Title2 as="h2" block>Substitute delegations</Title2>
          <Body1 as="p">
            Route a manager's pending approvals to a substitute while they're out. {delegations.length} total.
          </Body1>
        </div>
        <Button appearance="primary" onClick={() => setCreateOpen(true)}>
          New delegation
        </Button>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.filterChips}>
          {(["all", "active", "upcoming", "expired", "inactive"] as FilterKey[]).map((k) => (
            <Button
              key={k}
              size="small"
              appearance={filter === k ? "primary" : "secondary"}
              onClick={() => setFilter(k)}
            >
              {k.charAt(0).toUpperCase() + k.slice(1)} ({counts[k]})
            </Button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className={styles.empty}>
          <Body1>No delegations in this view.</Body1>
        </div>
      ) : (
        <Table aria-label="Substitute delegations">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Manager</TableHeaderCell>
              <TableHeaderCell>Substitute</TableHeaderCell>
              <TableHeaderCell>Dates</TableHeaderCell>
              <TableHeaderCell>State</TableHeaderCell>
              <TableHeaderCell aria-label="Actions" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((d) => (
              <TableRow key={d.id}>
                <TableCell>{d.managerName}</TableCell>
                <TableCell>{d.substituteName}</TableCell>
                <TableCell>{formatDateRange(d.startDate, d.endDate)}</TableCell>
                <TableCell>{stateBadge(d.state)}</TableCell>
                <TableCell style={{ whiteSpace: "nowrap", display: "flex", gap: tokens.spacingHorizontalS }}>
                  <Button size="small" appearance="subtle" onClick={() => setEditTarget(d)}>Edit dates</Button>
                  <Button size="small" appearance="subtle" onClick={() => handleToggleActive(d)}>
                    {d.isActive ? "Pause" : "Resume"}
                  </Button>
                  <Button size="small" appearance="subtle" onClick={() => setDeleteTarget(d)}>Delete</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <CreateDelegationDialog
        open={createOpen}
        users={activeUsers}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); load(); }}
      />

      <EditDelegationDialog
        delegation={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={() => { setEditTarget(null); load(); }}
      />

      <DeleteDelegationDialog
        delegation={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={() => { setDeleteTarget(null); load(); }}
      />
    </div>
  );
}

interface CreateProps {
  open: boolean;
  users: AdminUser[];
  onClose: () => void;
  onCreated: () => void;
}

function CreateDelegationDialog({ open, users, onClose, onCreated }: CreateProps) {
  const styles = useStyles();
  const [managerId, setManagerId] = useState<string>("");
  const [substituteId, setSubstituteId] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setManagerId("");
      setSubstituteId("");
      setStartDate("");
      setEndDate("");
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  const manager = users.find((u) => u.id === managerId);
  const substitute = users.find((u) => u.id === substituteId);
  const substituteHasManagerRole =
    !!substitute && (substitute.roles.includes("manager") || substitute.roles.includes("administrator"));

  const canSubmit =
    !!managerId &&
    !!substituteId &&
    managerId !== substituteId &&
    !!startDate &&
    !!endDate &&
    endDate >= startDate &&
    !submitting;

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetch("/api/manage-delegations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ managerId, substituteId, startDate, endDate })
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${r.status}`);
      }
      onCreated();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(_, d) => !d.open && onClose()} modalType="modal">
      <DialogSurface>
        <DialogBody>
          <DialogTitle>New substitute delegation</DialogTitle>
          <DialogContent>
            <div className={styles.dialogBody}>
              <Field label="Manager going out" required>
                <Combobox
                  placeholder="Pick a manager"
                  value={manager?.displayName ?? ""}
                  selectedOptions={managerId ? [managerId] : []}
                  onOptionSelect={(_, data) => setManagerId(data.optionValue ?? "")}
                >
                  {users.map((u) => (
                    <Option key={u.id} value={u.id} text={u.displayName}>
                      {u.displayName} — {u.email}
                    </Option>
                  ))}
                </Combobox>
              </Field>
              <Field label="Substitute" required>
                <Combobox
                  placeholder="Pick a substitute"
                  value={substitute?.displayName ?? ""}
                  selectedOptions={substituteId ? [substituteId] : []}
                  onOptionSelect={(_, data) => setSubstituteId(data.optionValue ?? "")}
                >
                  {users
                    .filter((u) => u.id !== managerId)
                    .map((u) => (
                      <Option key={u.id} value={u.id} text={u.displayName}>
                        {u.displayName} — {u.email}
                      </Option>
                    ))}
                </Combobox>
              </Field>
              {substitute && !substituteHasManagerRole && (
                <Body1 className={styles.warn}>
                  Heads up: {substitute.displayName} is not currently a manager. They'll be able to act on the
                  delegated approvals, but won't see the nav link unless you also grant them the manager role.
                </Body1>
              )}
              <div className={styles.dateRow}>
                <Field label="Start date" required style={{ flex: 1 }}>
                  <Input type="date" value={startDate} onChange={(_, d) => setStartDate(d.value)} />
                </Field>
                <Field label="End date" required style={{ flex: 1 }}>
                  <Input type="date" value={endDate} onChange={(_, d) => setEndDate(d.value)} />
                </Field>
              </div>
              {error && <Body1 className={styles.error}>{error}</Body1>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button appearance="primary" onClick={handleSubmit} disabled={!canSubmit}>
              {submitting ? "Creating…" : "Create delegation"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

interface EditProps {
  delegation: Delegation | null;
  onClose: () => void;
  onSaved: () => void;
}

function EditDelegationDialog({ delegation, onClose, onSaved }: EditProps) {
  const styles = useStyles();
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (delegation) {
      setStartDate(delegation.startDate);
      setEndDate(delegation.endDate);
      setIsActive(delegation.isActive);
      setError(null);
      setSubmitting(false);
    }
  }, [delegation]);

  if (!delegation) return null;
  const canSubmit = !!startDate && !!endDate && endDate >= startDate && !submitting;

  async function handleSave() {
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetch(`/api/manage-delegations/${encodeURIComponent(delegation!.id)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ startDate, endDate, isActive })
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${r.status}`);
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()} modalType="modal">
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Edit delegation</DialogTitle>
          <DialogContent>
            <div className={styles.dialogBody}>
              <Body1>
                <strong>{delegation.managerName}</strong> → <strong>{delegation.substituteName}</strong>
              </Body1>
              <div className={styles.dateRow}>
                <Field label="Start date" required style={{ flex: 1 }}>
                  <Input type="date" value={startDate} onChange={(_, d) => setStartDate(d.value)} />
                </Field>
                <Field label="End date" required style={{ flex: 1 }}>
                  <Input type="date" value={endDate} onChange={(_, d) => setEndDate(d.value)} />
                </Field>
              </div>
              <Field label="Active">
                <Switch
                  checked={isActive}
                  onChange={(_, d) => setIsActive(d.checked)}
                  label={isActive ? "Substitute can act during the window" : "Paused — no delegated routing"}
                />
              </Field>
              {error && <Body1 className={styles.error}>{error}</Body1>}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button appearance="primary" onClick={handleSave} disabled={!canSubmit}>
              {submitting ? "Saving…" : "Save"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

interface DeleteProps {
  delegation: Delegation | null;
  onClose: () => void;
  onDeleted: () => void;
}

function DeleteDelegationDialog({ delegation, onClose, onDeleted }: DeleteProps) {
  const styles = useStyles();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (delegation) {
      setSubmitting(false);
      setError(null);
    }
  }, [delegation]);

  if (!delegation) return null;

  async function handleDelete() {
    setSubmitting(true);
    setError(null);
    try {
      const r = await fetch(`/api/manage-delegations/${encodeURIComponent(delegation!.id)}`, { method: "DELETE" });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${r.status}`);
      }
      onDeleted();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && onClose()} modalType="modal">
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Delete delegation?</DialogTitle>
          <DialogContent>
            <Body1>
              Remove the delegation from <strong>{delegation.managerName}</strong> to{" "}
              <strong>{delegation.substituteName}</strong>?
            </Body1>
            <Body1 style={{ marginTop: tokens.spacingVerticalM, color: tokens.colorNeutralForeground3 }}>
              An audit-log entry is written with the full row snapshot, so this is recoverable. To pause
              the delegation without losing the record, use "Pause" instead.
            </Body1>
            {error && <Body1 className={styles.error}>{error}</Body1>}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button appearance="primary" onClick={handleDelete} disabled={submitting}>
              {submitting ? "Deleting…" : "Yes, delete"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
